import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Popover,
  Divider,
  Button,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Slide,
  Fade,
  ListItemButton,
  Tooltip,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  NotificationsOff,
  Circle,
  CheckCircle,
  Warning,
  Error,
  Info,
  ShoppingCart,
  Payment,
  LocalShipping,
  Star,
  Message,
  Business,
  Inventory,
  TrendingUp,
  Security,
  Settings,
  Clear,
  MarkAsUnread,
  Delete,
  Archive,
  FilterList,
  Refresh,
  Close,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import axios from '../../utils/axiosConfig';

const NotificationSystem = () => {
  const dispatch = useDispatch();
  const { user, token, isAuthenticated } = useSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [startupBannerVisible, setStartupBannerVisible] = useState(false);
  const [startupNotifications, setStartupNotifications] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({
    orders: true,
    payments: true,
    inventory: true,
    reviews: true,
    messages: true,
    system: true,
    marketing: false,
    sound: true,
    desktop: true,
    email: true,
    sms: false,
  });

  const wsRef = useRef(null);
  const bellButtonRef = useRef(null);
  const notificationIdsRef = useRef(new Set());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Re-initialize whenever the authenticated user changes (login/logout).
  // Use user?.id (stable primitive) instead of the full user object to avoid
  // re-triggering on every profile update that recreates the user object.
  useEffect(() => {
    if (!user?.id || !isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setStartupBannerVisible(false);
      setStartupNotifications([]);
      notificationIdsRef.current = new Set();
      if (wsRef.current) wsRef.current.close();
      return;
    }

    // Reset state for this user session
    setNotifications([]);
    setUnreadCount(0);
    setStartupBannerVisible(false);
    setStartupNotifications([]);
    notificationIdsRef.current = new Set();

    // Fetch persisted notifications from backend, then start SSE
    fetchNotifications();

    if (notificationSettings.desktop) {
      initializeWebSocket();
    }

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token, isAuthenticated]);

  // ── API helpers ──────────────────────────────────────────────────────────────

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/notifications');
      if (!mountedRef.current) return;

      const fetched = response.data.notifications || [];
      notificationIdsRef.current = new Set(fetched.map(n => n.id));
      setNotifications(fetched);
      setUnreadCount(fetched.filter(n => !n.read).length);

      // Show startup banner for notifications that are unread AND not yet shown
      const unshownUnread = fetched.filter(n => !n.read && !n.shown);
      if (unshownUnread.length > 0) {
        setStartupNotifications(unshownUnread);
        setStartupBannerVisible(true);
      }
    } catch (error) {
      console.error('[Notifications] Failed to fetch from backend:', error);
    }
  };

  const markNotificationAsShown = async (notificationId) => {
    try {
      await axios.post(`/notifications/${notificationId}/shown`);
    } catch (error) {
      console.error('[Notifications] Failed to mark as shown:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    // Optimistic UI update
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await axios.post(`/notifications/${notificationId}/read`);
    } catch (error) {
      console.error('[Notifications] Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await axios.post('/notifications/mark-all-read');
    } catch (error) {
      console.error('[Notifications] Failed to mark all as read:', error);
    }
  };

  // ── Startup banner handlers ──────────────────────────────────────────────────

  const handleDismissStartupBanner = async () => {
    setStartupBannerVisible(false);
    // Persist "shown" state so this banner never appears again for these notifications
    try {
      await axios.post('/notifications/mark-all-shown');
    } catch (error) {
      console.error('[Notifications] Failed to mark all as shown:', error);
    }
  };

  const handleViewStartupNotifications = () => {
    setAnchorEl(bellButtonRef.current);
    handleDismissStartupBanner();
  };

  // ── SSE connection ───────────────────────────────────────────────────────────

  const initializeWebSocket = () => {
    let eventSource = null;

    const connectSSE = () => {
      try {
        const authToken = token || localStorage.getItem('token');
        if (!authToken) {
          console.log('[SSE] No JWT token, cannot connect');
          return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
        eventSource = new EventSource(
          `${API_BASE_URL}/notifications/stream?token=${encodeURIComponent(authToken)}`,
          { withCredentials: true }
        );

        eventSource.onopen = () => console.log('[SSE] Connection opened');

        eventSource.addEventListener('notification', (event) => {
          try {
            const notification = JSON.parse(event.data);
            handleNewNotification(notification);
          } catch (err) {
            console.error('[SSE] Error parsing notification:', err);
          }
        });

        eventSource.addEventListener('connected', (event) => {
          console.log('[SSE] Connected:', event.data);
        });

        eventSource.onerror = () => {
          eventSource.close();
          // Reconnect after 5 s if still authenticated
          setTimeout(() => {
            if (mountedRef.current && user?.id && isAuthenticated) connectSSE();
          }, 5000);
        };
      } catch (err) {
        console.error('[SSE] Failed to establish connection:', err);
      }
    };

    connectSSE();

    wsRef.current = {
      close: () => { if (eventSource) eventSource.close(); },
    };
  };

  // ── Incoming live notification handler ───────────────────────────────────────

  const handleNewNotification = (notification) => {
    if (!mountedRef.current) return;

    // Deduplicate: skip if we already have this notification (from initial fetch or prior SSE)
    if (notificationIdsRef.current.has(notification.id)) return;
    notificationIdsRef.current.add(notification.id);

    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Mark as shown in backend — user saw it live via SSE, so it won't appear in startup banner
    markNotificationAsShown(notification.id);

    // Show in-app toast for all live notifications
    toast.info(`${notification.title}: ${notification.message}`, {
      position: 'top-right',
      autoClose: 6000,
    });

    // Browser notification (only if permission granted)
    if (notificationSettings.desktop && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/mos-logo.jpg',
        tag: notification.id,
      });
    }

    if (notificationSettings.sound) {
      const enableSound = process.env.REACT_APP_ENABLE_SOUND_NOTIFICATIONS === 'true';
      if (enableSound) {
        const audio = new Audio('/notification-sound.mp3');
        audio.play().catch(() => {});
      }
    }
  };

  // ── Local-only helpers ───────────────────────────────────────────────────────

  const deleteNotification = (notificationId) => {
    const n = notifications.find(n => n.id === notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (n && !n.read) setUnreadCount(prev => Math.max(0, prev - 1));
    notificationIdsRef.current.delete(notificationId);
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    notificationIdsRef.current = new Set();
  };

  // ── Display helpers ──────────────────────────────────────────────────────────

  const getFilteredNotifications = () => {
    let filtered = notifications;

    if (selectedFilter !== 'all') {
      const filterMap = {
        order: ['ORDER_STATUS_CHANGE', 'NEW_ORDER_PLACED', 'NEW_CUSTOMER_ORDER'],
        payment: ['PAYMENT_RECEIVED', 'PAYMENT_FAILED'],
        inventory: ['WISHLIST_OUT_OF_STOCK'],
        review: ['REVIEW_RECEIVED'],
        visit: ['VISIT_REQUEST', 'NEW_VISIT_REQUEST', 'VISIT_STATUS_UPDATE', 'VISIT_COMPLETED', 'VISIT_CANCELLED'],
      };
      const types = filterMap[selectedFilter] || [];
      filtered = filtered.filter(n => types.includes(n.type));
    }

    if (activeTab === 1) {
      filtered = filtered.filter(n => !n.read);
    }

    return filtered;
  };

  const getNotificationIcon = (type) => {
    if (!type) return <Info />;
    const t = type.toLowerCase();
    if (t.includes('order')) return <ShoppingCart />;
    if (t.includes('payment')) return <Payment />;
    if (t.includes('stock') || t.includes('inventory')) return <Inventory />;
    if (t.includes('review')) return <Star />;
    if (t.includes('visit')) return <TrendingUp />;
    if (t.includes('product') || t.includes('seller')) return <Business />;
    if (t.includes('message')) return <Message />;
    return <Info />;
  };

  const getNotificationColor = (type) => {
    if (!type) return 'default';
    const t = type.toLowerCase();
    if (t.includes('approved') || t.includes('completed') || t.includes('placed')) return 'success';
    if (t.includes('rejected') || t.includes('cancelled') || t.includes('failed')) return 'error';
    if (t.includes('stock') || t.includes('warning')) return 'warning';
    if (t.includes('order')) return 'primary';
    return 'info';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const time = new Date(timestamp);
    if (isNaN(time.getTime())) return '';
    const diff = Date.now() - time.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.actionUrl) window.location.href = notification.actionUrl;
    setAnchorEl(null);
  };

  // ── Sub-components ───────────────────────────────────────────────────────────

  const NotificationItem = ({ notification }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
    >
      <ListItem
        disablePadding
        secondaryAction={
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              deleteNotification(notification.id);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        }
      >
        <ListItemButton
          onClick={() => handleNotificationClick(notification)}
          sx={{
            bgcolor: notification.read ? 'transparent' : 'action.hover',
            borderLeft: notification.read ? 'none' : '4px solid',
            borderLeftColor: `${getNotificationColor(notification.type)}.main`,
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          <ListItemIcon>
            <Avatar
              sx={{
                bgcolor: `${getNotificationColor(notification.type)}.main`,
                width: 40,
                height: 40,
              }}
            >
              {getNotificationIcon(notification.type)}
            </Avatar>
          </ListItemIcon>

          <ListItemText
            disableTypography
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: notification.read ? 400 : 600, flex: 1 }}
                >
                  {notification.title}
                </Typography>
                {!notification.read && (
                  <Circle sx={{ fontSize: 8, color: 'primary.main' }} />
                )}
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {notification.message}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatTimestamp(notification.createdAt)}
                  </Typography>
                  <Chip
                    label={notification.type}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
    </motion.div>
  );

  if (!user) return null;

  return (
    <>
      {/* Startup notification banner — shown once per session for unread+unshown notifications */}
      {startupBannerVisible && startupNotifications.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            p: 0,
          }}
        >
          <Alert
            severity="info"
            sx={{ borderRadius: 0, boxShadow: 3 }}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  size="small"
                  color="inherit"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', py: 0.25 }}
                  onClick={handleViewStartupNotifications}
                >
                  View
                </Button>
                <IconButton size="small" color="inherit" onClick={handleDismissStartupBanner}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            {startupNotifications.length === 1 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} component="span">
                  {startupNotifications[0].title}
                </Typography>
                {' — '}
                <Typography variant="body2" component="span">
                  {startupNotifications[0].message.length > 80
                    ? startupNotifications[0].message.substring(0, 80) + '…'
                    : startupNotifications[0].message}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body1">
                You have <strong>{startupNotifications.length}</strong> unread notifications since your last visit.
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* Notification Bell */}
      <Tooltip title="Notifications">
        <IconButton
          ref={bellButtonRef}
          aria-label="notifications"
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ position: 'relative', display: { xs: 'none', sm: 'flex' } }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? <NotificationsActive /> : <Notifications />}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Notification Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 380 }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Notifications
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                  <FilterList />
                </IconButton>
                <IconButton size="small" onClick={fetchNotifications} title="Refresh">
                  <Refresh />
                </IconButton>
                <IconButton size="small" onClick={() => setSettingsOpen(true)}>
                  <Settings />
                </IconButton>
              </Box>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{ mt: 1 }}
            >
              <Tab label="All" />
              <Tab label={`Unread (${unreadCount})`} />
            </Tabs>
          </Box>

          {/* Bulk actions */}
          {notifications.length > 0 && (
            <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<CheckCircle />}
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                >
                  Mark all read
                </Button>
                <Button
                  size="small"
                  startIcon={<Clear />}
                  onClick={clearAllNotifications}
                  color="error"
                >
                  Clear all
                </Button>
              </Box>
            </Box>
          )}

          {/* Notification list */}
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getFilteredNotifications().length > 0 ? (
              <List disablePadding>
                <AnimatePresence>
                  {getFilteredNotifications().map((notification, index, arr) => (
                    <React.Fragment key={notification.id}>
                      <NotificationItem notification={notification} />
                      {index < arr.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </AnimatePresence>
              </List>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <NotificationsOff sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No notifications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You're all caught up!
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Popover>

      {/* Filter menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        {[
          { key: 'all', label: 'All Types' },
          { key: 'order', label: 'Orders' },
          { key: 'payment', label: 'Payments' },
          { key: 'inventory', label: 'Inventory' },
          { key: 'review', label: 'Reviews' },
          { key: 'visit', label: 'Visit Requests' },
        ].map(({ key, label }) => (
          <MenuItem
            key={key}
            selected={selectedFilter === key}
            onClick={() => { setSelectedFilter(key); setFilterAnchorEl(null); }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notification Settings</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Notification Types
          </Typography>
          {Object.entries({
            orders: 'Order notifications',
            payments: 'Payment notifications',
            inventory: 'Inventory alerts',
            reviews: 'Review notifications',
            messages: 'Messages',
            system: 'System updates',
            marketing: 'Marketing emails',
          }).map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={notificationSettings[key]}
                  onChange={(e) =>
                    setNotificationSettings(prev => ({ ...prev, [key]: e.target.checked }))
                  }
                />
              }
              label={label}
              sx={{ display: 'block', mb: 1 }}
            />
          ))}

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
            Delivery Methods
          </Typography>
          {Object.entries({
            sound: 'Sound notifications',
            desktop: 'Desktop notifications',
            email: 'Email notifications',
            sms: 'SMS notifications',
          }).map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={notificationSettings[key]}
                  onChange={(e) =>
                    setNotificationSettings(prev => ({ ...prev, [key]: e.target.checked }))
                  }
                />
              }
              label={label}
              sx={{ display: 'block', mb: 1 }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              toast.success('Notification settings saved!');
              setSettingsOpen(false);
            }}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationSystem;
